import { useDevices, useLocations, useSchedules } from "@repo/data-store";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { openOverlay } from "@/app/overlays";
import { DEFAULT_CALENDAR_SETTINGS, ScheduleCalendar } from "@/app/surfaces/schedule-calendar";
import {
  filterPractitionerSchedules,
  formatLocalDate,
  loadPrefs,
  PractitionerToolbarActions,
  parseScheduleIds,
  ResourceTimelineView,
  ScheduleToolbar,
  ScheduleToolbarNav,
  useCalendarSettings,
  useScheduleActions,
  useScheduleDate,
  useScheduleInitialized,
  useTimelineMode,
} from "@/features/scheduling";
import { CalendarSettingsDialog } from "@/features/scheduling/components/calendar/settings-dialog";
import type { ScheduleCalendarApi } from "@/features/scheduling/components/calendar/types";
import { practitionerDomainIdFromProfile } from "@/shared/hooks/use-current-practitioner";

const scheduleSearchSchema = z.object({
  date: z.string().optional(),
  view: z.enum(["week", "month", "day"]).catch("week"),
});

export const Route = createFileRoute("/schedule/")({
  validateSearch: scheduleSearchSchema,
  beforeLoad: ({ search }) => {
    if (!search.date) {
      const stored = localStorage.getItem("schedule-preferences");
      if (stored) {
        const prefs = JSON.parse(stored);
        if (prefs.date) {
          throw redirect({
            to: "/schedule",
            search: {
              date: prefs.date,
              view: prefs.view || "week",
            },
            replace: true,
          });
        }
      }
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { date, view } = Route.useSearch();
  const { profile } = Route.useRouteContext();
  const practitionerId = practitionerDomainIdFromProfile(profile);
  const currentUserRef = practitionerId ? `Practitioner/${practitionerId}` : null;
  const initRef = useRef(false);
  // Shared with ScheduleCalendar: populated with the ReUI calendar's imperative
  // api once mounted, so the toolbar's Prev/Next/Today delegate to it (MIN-252).
  const calendarApiRef = useRef<ScheduleCalendarApi | null>(null);
  const { init, setAllIds, setTimelineMode, setCalendarSettings } = useScheduleActions();
  const initialized = useScheduleInitialized();
  const timelineMode = useTimelineMode();
  const anchorDate = useScheduleDate();
  const calendarSettings = useCalendarSettings();

  // Fetch all schedules + resources; restrict to Practitioner-actor schedules only
  const { schedules: allSchedules } = useSchedules();
  const { locations } = useLocations();
  const { devices } = useDevices();
  const schedules = useMemo(() => filterPractitionerSchedules(allSchedules), [allSchedules]);
  const allScheduleIds = useMemo(() => schedules.map((s) => s.scheduleId), [schedules]);

  // Appointment-create + settings dialogs live at the page level so both the
  // toolbar and the calendar (slot-click) can open them.
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleNewAppointment = (initialDate?: Date, initialTime?: string) => {
    openOverlay("appointment.book", { initialDate, initialTime });
  };

  const handleNewConsultation = () => {
    openOverlay("appointment.book", { initialFlow: "consult" });
  };

  // One-time init into store — guard prevents re-execution, so only allScheduleIds matters
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot effect guarded by initRef
  useEffect(() => {
    if (initRef.current || allScheduleIds.length === 0) return;
    initRef.current = true;

    const dateObj = date ? new Date(`${date}T00:00:00`) : new Date();
    const dateStr = date || formatLocalDate(new Date());
    const prefs = loadPrefs();

    // Derive selected schedule IDs at init time.
    // Priority: last saved selection (localStorage) > user's own schedule > first
    const fromPrefs = prefs?.scheduleIds ? parseScheduleIds(prefs.scheduleIds, allScheduleIds) : undefined;
    const userScheduleId = currentUserRef
      ? schedules.find((s) => `${s.actor.type}/${s.actor.id}` === currentUserRef)?.scheduleId
      : undefined;
    const selectedIds =
      (fromPrefs && fromPrefs.length > 0 && fromPrefs) ||
      (userScheduleId ? [userScheduleId] : null) ||
      (allScheduleIds.length > 0 ? [allScheduleIds[0]] : []);

    init({
      date: dateObj,
      dateStr,
      view,
      selectedScheduleIds: selectedIds,
      allScheduleIds,
      currentUserRef,
      calendarSettings: prefs?.calendarSettings ?? DEFAULT_CALENDAR_SETTINGS,
    });
  }, [allScheduleIds, init]);

  // Keep allScheduleIds in sync when schedules load/change
  useEffect(() => {
    if (!initRef.current) return;
    setAllIds(allScheduleIds);
  }, [allScheduleIds, setAllIds]);

  return (
    <div className="flex h-full flex-col">
      <ScheduleToolbar.Root mode={timelineMode} onModeChange={setTimelineMode} className="border-b px-3 py-2">
        <ScheduleToolbar.Left>
          <ScheduleToolbarNav apiRef={calendarApiRef} />
        </ScheduleToolbar.Left>
        <ScheduleToolbar.ModeTabs />
        <ScheduleToolbar.Right>
          {timelineMode === "resources" ? null : (
            <PractitionerToolbarActions
              schedules={schedules}
              onNewAppointment={() => handleNewAppointment()}
              onNewConsultation={handleNewConsultation}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          )}
        </ScheduleToolbar.Right>
      </ScheduleToolbar.Root>

      <div className="min-h-0 flex-1">
        {initialized ? (
          timelineMode === "resources" ? (
            <ResourceTimelineView locations={locations} devices={devices} anchorDate={anchorDate} />
          ) : (
            <ScheduleCalendar
              schedules={schedules}
              locations={locations}
              devices={devices}
              onNewAppointment={handleNewAppointment}
              apiRef={calendarApiRef}
            />
          )
        ) : null}
      </div>

      <CalendarSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={calendarSettings}
        onSettingsChange={setCalendarSettings}
      />
    </div>
  );
}
